class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.drawerNav = document.querySelector(".wt-drawer--nav");
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.drawerNavAnimationOffClass = "wt-drawer--off-animations";
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.searchProperties = this.dataset.searchProperties;

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.statusElement = this.querySelector(".predictive-search-status");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);

    this.isPredictiveSearchAvailable = this.hidePredictiveSearch();
    this.isPredictiveSearchEnabled =
      this.dataset.enablePredictiveSearch === "true";

    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.timeOut = null;
    this.setAttribute("empty", true);
    this.init();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  openSearch() {
    if (this.isOpen) return;

    this.setAttribute("open", "");
    setTabindex(this.toggleTabindexElements, "0");
    setTabindex([this.mainTrigger], "-1");
    this.isOpen = true;
    this.setAttribute("empty", true);

    this.drawer.classList.add(this.classDrawerActive);
    this.drawerNav?.classList.add(this.drawerNavAnimationOffClass);
    this.body.classList.add(this.activeOverlayBodyClass);

    this.input.focus();
    this.input.setSelectionRange(
      this.input.value.length,
      this.input.value.length,
    );

    if (this.getQuery().length) {
      this.getSearchResults(this.getQuery());
      this.removeAttribute("empty");
    }
  }

  closeSearch() {
    if (!this.isOpen) return;

    this.removeAttribute("open");
    setTabindex(this.toggleTabindexElements, "-1");
    setTabindex([this.mainTrigger], "0");
    this.isOpen = false;

    this.drawer.classList.remove(this.classDrawerActive);
    this.drawerNav?.classList.remove(this.drawerNavAnimationOffClass);
    this.body.classList.remove(this.activeOverlayBodyClass);
    this.clearResults();

    if (this.clearButton) {
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
    }

    setTimeout(() => {
      this.mainTrigger?.focus();
    }, 0);
  }

  toggleSearch() {
    if (this.isOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (trigger.classList.contains("wt-header__search__close")) {
          this.closeSearch();
        } else {
          this.toggleSearch();
        }
      });
    });

    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => {
        this.input.value = "";
        this.clearButton.style.display = "none";
        this.isVisibleClearButton = false;
        this.clearResults();
        this.input.focus();
      });
    }

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.closeSearch();
        }
      }

      if (isTabPressed && this.isOpen) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (!this.clearButton) return;
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  hidePredictiveSearch() {
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        return false;
      }
    }
    return true;
  }

  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    if (this.isPredictiveSearchAvailable && this.isPredictiveSearchEnabled) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.add(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (!this.isOpen) {
      this.openSearch();
    }

    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults({ keepInput: true });
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Same storefront search as the search page so drawer + results stay in sync.
    const params = new URLSearchParams();
    params.set("q", searchTerm);
    params.set("type", "product");
    params.set("section_id", "predictive-search");

    const searchUrl =
      (window.routes && window.routes.search_url) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? `${window.Shopify.routes.root}search`
        : "/search");

    fetch(`${searchUrl}?${params.toString()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== searchTerm) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    const textEl =
      this.statusElement.querySelector(".predictive-search-status__text") ||
      this.statusElement;
    textEl.textContent = statusText || "";

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.removeAttribute("empty");
    this.animateResultsIn();
    this.syncSearchCarouselOffsets();
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  getSearchInlinePad() {
    const results = this.querySelector(".wt-header__search__results");
    if (results) {
      const probe = document.createElement("div");
      probe.style.cssText =
        "position:absolute;visibility:hidden;pointer-events:none;padding-left:var(--search-inline-pad)";
      results.appendChild(probe);
      const pad = parseFloat(getComputedStyle(probe).paddingLeft);
      probe.remove();
      if (!Number.isNaN(pad) && pad > 0) return pad;
    }

    const aside = this.querySelector(".search-result__aside");
    if (aside) {
      const pad = parseFloat(getComputedStyle(aside).paddingLeft);
      if (!Number.isNaN(pad) && pad > 0) return pad;
    }

    return 16;
  }

  syncSearchCarouselOffsets() {
    const slideshow = this.querySelector(
      ".search-result__carousel slideshow-section",
    );
    if (!slideshow) return;

    const apply = () => {
      const swiper = slideshow.swiper;
      if (!swiper) return false;

      const pad = this.getSearchInlinePad();
      const isDesktop = window.matchMedia("(min-width: 900px)").matches;
      // Mobile: inset both ends (bleed while scrolling, pad on last/first).
      // Desktop: track already starts after aside; only pad the end.
      const before = isDesktop ? 0 : pad;
      const after = pad;

      if (
        swiper.params.slidesOffsetBefore === before &&
        swiper.params.slidesOffsetAfter === after
      ) {
        return true;
      }

      swiper.params.slidesOffsetBefore = before;
      swiper.params.slidesOffsetAfter = after;
      swiper.update();
      if (swiper.isEnd) {
        swiper.slideTo(swiper.slides.length - 1, 0);
      }
      return true;
    };

    if (apply()) return;

    const wait = setInterval(() => {
      if (apply()) clearInterval(wait);
    }, 50);
    setTimeout(() => clearInterval(wait), 3000);

    if (!this._searchCarouselResizeBound) {
      this._searchCarouselResizeBound = true;
      window.addEventListener(
        "resize",
        debounce(() => this.syncSearchCarouselOffsets(), 150),
      );
    }
  }

  animateResultsIn() {
    const results = this.predictiveSearchResults.querySelector(".search-result");
    if (!results) return;

    results.classList.remove("search-result--enter");
    void results.offsetWidth;
    results.classList.add("search-result--enter");
  }

  clearResults({ keepInput = false } = {}) {
    if (!keepInput) {
      this.input.value = "";
    }
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = "";
    }
    this.setLiveRegionText("");
    clearTimeout(this.timeOut);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
